import { ReactNode, useState } from 'react';

import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { faCrown, faLock } from '@fortawesome/pro-solid-svg-icons';
import Router from 'next/router';

import { analytics } from '@modules/analytics';

import Tooltip from '@components/Tooltip/Tooltip';
import Popover, { positionBounded } from '@components/Popover/Popover';

import { useUser } from '@context/user-context';
import { isMemberCardCreateEnabled } from '@modules/user-helpers';
import useWorkspaceUrl from '@hooks/use-workspace-url';
import useNode from '@hooks/use-node';

import type { Card, CardPreset } from 'types';

const DEFAULT_CARD_TITLE = 'Work';
const DEFAULT_CARD_PRESET_NAME = 'Company';

import styles from './SelectorBar.module.scss';
import { faExclamationCircle } from '@fortawesome/pro-duotone-svg-icons';
import Button from '@components/Button/Button';
import Dialog from '@components/Dialog/Dialog';
import { useTeamRole } from '@hooks/team-workspace';
import { useActorPlan } from '@hooks/actor/use-actor-plan';
import { ModalType, useGlobalModal } from '@modules/global-modals';

interface SelectorBarProps {
  title: string;
  addNewButton?: React.ReactElement;
  mode?: 'card' | 'template';
  children?: ReactNode;
}

const SelectorBar = ({ title, addNewButton, children, mode = 'card' }: SelectorBarProps): JSX.Element => {
  const user = useUser();
  let showAddNewBtn = true;
  switch (mode) {
    case 'card':
      showAddNewBtn = isMemberCardCreateEnabled(user);
      break;
    case 'template':
    default:
      showAddNewBtn = true;
      break;
  }

  return (
    <div className={styles['selector-bar']} data-mode={mode}>
      <div className={styles['selector-bar-contents']}>
        <div className={styles['selector-bar-header']}>
          <div className={styles['selector-bar-title']}>{title}</div>
          {showAddNewBtn && addNewButton ? addNewButton : null}
        </div>
        <ul className={styles['selector-bar-items']}>{children}</ul>
      </div>
    </div>
  );
};

interface SelectorBarAddNewButtonProps {
  locked?: boolean;
}

export const SelectorBarAddNewCardButton: React.FC<React.PropsWithChildren<SelectorBarAddNewButtonProps>> = ({
  locked = false,
}) => {
  const { atLeastRole } = useTeamRole();
  const { planType } = useActorPlan();
  const isTeamAdmin = atLeastRole('ADMIN');
  const { openModal } = useGlobalModal({
    trackingSource: 'selector-bar',
    registeredModals: [ModalType.UPGRADE],
  });

  const [showCardTypeSelection, setShowCardTypeSelection] = useState(false);
  const [showMaxCardsReachedNotification, setShowMaxCardsReachedNotification] = useState(false);
  const newPersonalCardUrl = useWorkspaceUrl('cards/new/personal');
  const teamPageUrl = useWorkspaceUrl('team');
  const newTeamCardUrl = useWorkspaceUrl('cards/new/team');
  const [targetNode, targetRef] = useNode<HTMLButtonElement>();
  const addCardLockTooltipLabel =
    planType === 'free'
      ? 'Upgrade to a Blinq Premium or Blinq Business account to create more cards'
      : 'You have reached the maximum number of personal cards';

  function handleCreateCard(cardType: 'personal' | 'team') {
    const canCreateTeamCard = isTeamAdmin;
    if (cardType === 'team') {
      analytics.track({ name: 'teamcreatecardbutton_clicked' });
    }
    if (cardType === 'team' && !canCreateTeamCard) {
      setShowCardTypeSelection(false);
      openModal(ModalType.UPGRADE);
    } else if (cardType === 'team' && canCreateTeamCard) {
      Router.push(newTeamCardUrl);
    } else {
      Router.push(newPersonalCardUrl);
    }
  }

  function handleAddNewCardBtnClick() {
    if (locked && planType === 'free') {
      // If they are on a free plan and have created 2 cards then they need to upgrade
      openModal(ModalType.UPGRADE);
    } else if (locked && (planType === 'premium' || isTeamAdmin)) {
      // If it's locked and they are on a premium plan then there is a good chance they were trying to add cards
      // for their team on their personal page. And if it's locked and they are an admin of a business plan then we need to
      // let them know and reroute them to the team cards page.
      setShowMaxCardsReachedNotification(true);
    } else if (!locked && planType === 'team' && !isTeamAdmin) {
      // If they can add more cards AND are in a team but are not the admin just go straight to the
      // create personal card page
      Router.push(newPersonalCardUrl);
    } else if (!locked) {
      // If they can add another card and they are not a team member then give them the option to choose
      // who they would like to create a card for.
      setShowCardTypeSelection(true);
    }

    // If we are here it means it was locked and the user was in a team but wasn't the admin. At this point they have no
    // real options other than to delete cards so we are going to do nothing.
  }

  function handleViewBlinqBusinessFeatures() {
    setShowMaxCardsReachedNotification(false);
    openModal(ModalType.UPGRADE);
  }

  function handleGoToTeamCardPage() {
    Router.push(teamPageUrl);
  }

  return (
    <>
      {!locked && (
        <button className={styles['add-new-btn']} onClick={handleAddNewCardBtnClick} ref={targetRef}>
          <FontAwesomeIcon icon={faPlus} />
        </button>
      )}
      {locked && (
        <Tooltip size="small" label={addCardLockTooltipLabel} aria-label="Feature lock message">
          <button className={styles['add-new-btn']} data-locked="true" onClick={handleAddNewCardBtnClick}>
            <FontAwesomeIcon icon={faPlus} />
            <FontAwesomeIcon icon={faCrown} className={styles['card-lock']} />
          </button>
        </Tooltip>
      )}

      <Popover
        positioner={positionBounded({ offsetY: 8 })}
        targetNode={targetNode}
        show={showCardTypeSelection}
        onClose={() => setShowCardTypeSelection(false)}
        preventOutsideInteraction
        drawFocus
      >
        <div className={styles['card-type-selection-popover']}>
          <h3 className={styles['card-type-selection-popover-header']}>Who is this card for?</h3>
          <div className={styles['card-type-selection-popover-btns']}>
            <button
              className={styles['card-type-selection-popover-btn']}
              onClick={() => handleCreateCard('personal')}
            >
              Me
            </button>
            <button
              className={styles['card-type-selection-popover-btn']}
              onClick={() => handleCreateCard('team')}
            >
              <span>A team member</span>
              {!isTeamAdmin && (
                <FontAwesomeIcon icon={faCrown} className={styles['team-card-creation-lock']} />
              )}
            </button>
          </div>
        </div>
      </Popover>

      <Dialog
        show={showMaxCardsReachedNotification}
        ariaLabel="Max cards reached notification"
        onClose={() => {
          setShowMaxCardsReachedNotification(false);
        }}
      >
        <div className={styles['max-cards-reached-notification-dialog']}>
          <div className={styles['heading-container']}>
            <div className={styles['heading-icon']}>
              <FontAwesomeIcon icon={faExclamationCircle} />
            </div>
            <h3 className={styles['heading']}>Maximum number of personal cards reached</h3>
          </div>
          {planType === 'premium' && (
            <>
              <p className={styles['message']}>
                You have reached the limit for personal cards. If you are trying to create cards for your team
                you need to upgrade to a business plan.
              </p>
              <div className={styles['max-cards-reached-notification-action-btn-container']}>
                <Button submit size="large" onClick={handleViewBlinqBusinessFeatures}>
                  View Blinq Business features
                </Button>
              </div>
            </>
          )}
          {planType === 'team' && (
            <>
              <p className={styles['message']}>
                You have reached the limit for personal cards. If you are trying to create cards for your team
                you can go to the Team cards page and click <b>Create new team card</b>.
              </p>
              <div className={styles['max-cards-reached-notification-action-btn-container']}>
                <Button submit size="large" onClick={handleGoToTeamCardPage}>
                  Go to Team Cards page
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog>
    </>
  );
};

export const SelectorBarAddNewPresetButton: React.FC<
  React.PropsWithChildren<{ id: string; onClick?: () => void }>
> = ({ id, onClick }) => {
  const newPresetUrl = useWorkspaceUrl('presets/new');

  return <SelectorBarAddNewButton id={id} onClick={onClick} url={newPresetUrl}></SelectorBarAddNewButton>;
};

const SelectorBarAddNewButton: React.FC<
  React.PropsWithChildren<{ id: string; onClick?: () => void; url: string }>
> = ({ id, onClick, url }) => {
  return (
    <Link href={url} legacyBehavior>
      <a
        className={styles['add-new-btn']}
        id={id}
        onClick={onClick}
        onKeyDown={onClick}
        role="button"
        tabIndex={0}
      >
        <FontAwesomeIcon icon={faPlus} />
      </a>
    </Link>
  );
};

interface SelectorBarItemProps {
  name: string;
  href: string;
  selected: boolean;
  isActive?: boolean;
  isEditable?: boolean;
}

const SelectorBarItem: React.FC<React.PropsWithChildren<SelectorBarItemProps>> = ({
  name,
  href,
  selected,
  isActive = true,
  isEditable = true,
}) => {
  return (
    <li className={styles['selector-bar-item']} data-selected={selected}>
      <Link href={href} shallow legacyBehavior>
        <a className={styles['selector-bar-item-name']}>
          <div className={styles['selector-bar-item-name-content']}>{name}</div>
          {!isActive && (
            <div className={styles['selector-bar-item-not-active-badge']}>
              <FontAwesomeIcon icon={faLock} />
            </div>
          )}
          {!isEditable && isActive && (
            <Tooltip
              size="small"
              label="The Template owner has locked this card"
              aria-label="Feature lock message"
            >
              <div className={styles['selector-bar-item-not-editable-badge']}>
                <FontAwesomeIcon icon={faLock} />
              </div>
            </Tooltip>
          )}
        </a>
      </Link>
    </li>
  );
};

/**
 * Gets the list of card options to show in the selector. We make sure the current cards metadata takes
 * priority over any card with a matching ID in the list of cards. This means any updates you make to the
 * current card will be reflected in the list of all cards.
 * @param cards The complete list of cards that should be shown
 * @param currentCardMetadata The metadata of the card whose page the user is currently on
 */
export function getCardSelectorOptions(
  cards: Card[],
  currentCardMetadata: { cardId: string; isInvisible: boolean; cardTitle: string } | undefined
): { id: string; name: string }[] {
  if (cards.length === 0 && !currentCardMetadata) return [];
  else if (cards.length === 0 && currentCardMetadata && currentCardMetadata.isInvisible !== true) {
    return [{ id: currentCardMetadata.cardId, name: currentCardMetadata.cardTitle || DEFAULT_CARD_TITLE }];
  } else {
    return cards.map(({ cardId, cardTitle }) => {
      if (currentCardMetadata && cardId === currentCardMetadata.cardId) {
        return { id: cardId, name: currentCardMetadata.cardTitle || DEFAULT_CARD_TITLE };
      } else return { id: cardId, name: cardTitle || DEFAULT_CARD_TITLE };
    });
  }
}

/**
 * Gets the list of live template options to show in the selector. We make sure the current card presets metadata takes
 * priority over any live template with a matching ID. This means any updates you make to the current card preset will
 * be reflected in the list of all cards.
 * @param cardPresets The complete list of live template that should be shown
 * @param currentCardPresetMetadata The metadata of the live template whose page the user is currently on
 */
export function getCardPresetSelectorOptions(
  cardPresets: CardPreset[],
  currentCardPresetMetadata:
    | { cardPresetId: string; isInvisible: boolean; cardPresetName: string }
    | undefined
): { id: string; name: string }[] {
  if (cardPresets.length === 0 && !currentCardPresetMetadata) return [];
  else if (cardPresets.length === 0 && currentCardPresetMetadata) {
    return [
      {
        id: currentCardPresetMetadata.cardPresetId,
        name: currentCardPresetMetadata.cardPresetName || DEFAULT_CARD_PRESET_NAME,
      },
    ];
  } else {
    return cardPresets.map(({ cardPresetId, cardPresetName }) => {
      if (currentCardPresetMetadata && cardPresetId === currentCardPresetMetadata.cardPresetId) {
        return {
          id: cardPresetId,
          name: currentCardPresetMetadata.cardPresetName || DEFAULT_CARD_PRESET_NAME,
        };
      } else return { id: cardPresetId, name: cardPresetName || DEFAULT_CARD_PRESET_NAME };
    });
  }
}

export default SelectorBar;
export { SelectorBarItem };
